import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import pageStyles from './BranchDetail.module.css';
import tableStyles from '../components/branches/SectionTable.module.css';
import SectionTable from '../components/branches/SectionTable';
import ListPageHeader from '../components/common/ListPageHeader';
import Pill from '../components/common/Pill';
import ActionsForReleaseList from '../components/releases/ActionsForReleaseList';
import NewReleaseDialog from '../components/dialog/NewReleaseDialog';
import { apiGetReleasesList } from '../api/releases';
import { statusLabel } from '../components/branches/statusConfig';
import { timeSinceTS } from '../helpers/dates';
import { sortReleases } from '../helpers/releases';
import { WebSocketContext } from '../api/socket';

const ICO_ROCKET = 'M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09zM12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z';
const ICO_PLUS   = 'M12 5v14M5 12h14';

const PULSING_STATUSES = ['testing', 'validating', 'releasing', 'branching'];

const COLUMNS = [
  { label: 'Release',  width: '17%' },
  { label: 'Status',   width: '16%' },
  { label: 'Parent',   width: '16%' },
  { label: 'Released', width: '16%' },
  { label: 'Actions',  width: '35%' },
];

function renderRow(release, reloadData) {
  const parent = release.branch_version || release.source_branch || '—';
  return (
    <>
      <td className={tableStyles.td}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span className={tableStyles.mono}>{release.version}</span>
          {release.is_latest && <Pill kind="info">LATEST</Pill>}
        </span>
      </td>
      <td className={tableStyles.td}>
        <Pill status={release.status} dot pulse={PULSING_STATUSES.includes(release.status)}>
          {statusLabel(release.status)}
        </Pill>
      </td>
      <td className={tableStyles.td}>
        <span className={tableStyles.mono} style={{ fontSize: 12.5 }}>{parent}</span>
      </td>
      <td className={tableStyles.td}>
        {release.status === 'released' && release.release_date
          ? timeSinceTS(release.release_date)
          : <span className={tableStyles.muted}>—</span>}
      </td>
      <td className={tableStyles.td} onClick={(e) => e.stopPropagation()}>
        <ActionsForReleaseList release={release} reloadData={reloadData} />
      </td>
    </>
  );
}

export default function ReleasesList({ user }) {
  const navigate = useNavigate();
  const ws = useContext(WebSocketContext);
  const [releases, setReleases] = useState({ loaded: false, list: [] });
  const [newReleaseOpen, setNewReleaseOpen] = useState(false);

  const reloadData = () => {
    apiGetReleasesList().then((r) => {
      setReleases({ loaded: true, list: r.releases.sort(sortReleases) });
    }).catch((e) => {
      setReleases({ loaded: true, list: [], error: e });
    });
  };

  useEffect(() => {
    reloadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!ws) return;
    ws.on('release_update', reloadData);
    return () => { ws.off('release_update'); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws]);

  if (!releases.loaded) {
    return (
      <div className={pageStyles.loading}>
        <div className={pageStyles.loadingSpinner} />
        Loading releases…
      </div>
    );
  }

  const latest = releases.list.find((r) => r.is_latest);

  return (
    <div className={pageStyles.page}>
      <div className={pageStyles.inner}>
        <ListPageHeader
          title="Releases"
          subtitle={`${releases.list.length} release${releases.list.length === 1 ? '' : 's'}${latest ? ` · ${latest.version} latest` : ''}`}
          onReload={reloadData}
        />
        <SectionTable
          id="releases-table"
          icon={ICO_ROCKET}
          title="All releases"
          searchPlaceholder="Search releases…"
          searchFilter={(r, q) => r.version.toLowerCase().includes(q.toLowerCase())}
          triggerLabel="Create new"
          triggerIcon={ICO_PLUS}
          onTrigger={() => setNewReleaseOpen(true)}
          items={releases.list}
          columns={COLUMNS}
          renderRow={(r) => renderRow(r, reloadData)}
          getKey={(r) => r.version}
          onOpenItem={(r) => navigate(`/releases/${r.version}`)}
          pageSize={15}
          emptyTitle="No releases yet"
          emptyHint="Create a release to get started."
        />
      </div>
      <NewReleaseDialog
        open={newReleaseOpen}
        user={user}
        onClose={() => { setNewReleaseOpen(false); reloadData(); }}
      />
    </div>
  );
}
